import React, { useEffect } from 'react';
import { Select, Modal } from 'antd';
import { XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, AreaChart, Area, Legend, ReferenceLine } from 'recharts';
import dayjs from 'dayjs';
import TravelTimeIcon from '../../assets/Anomaly.svg';
import { ExpandButton } from '../../components';
import { createAverageLabel } from '../../components/charts/AverageReferenceLabel';
import '../../styles/Proprieta.css';

// Time axis ticks configuration
const TIME_TICKS = ['00:00', '04:00', '08:00', '12:00', '16:00', '20:00', '23:59'];

// Unique colors for each selected date
const DATE_COLORS = ['#4CAF50', '#1E88E5', '#FDD835', '#FB8C00', '#26C6DA', '#AB47BC', '#E53935'];
// Color constants
const COLOR_AVG = '#999999';

// Helper for stable color assignment
const getStableColor = (dateStr, palette) => {
  if (!dateStr || !palette || palette.length === 0) return '#000';
  let hash = 0;
  for (let i = 0; i < dateStr.length; i++) {
    hash = ((hash << 5) - hash) + dateStr.charCodeAt(i);
    hash |= 0;
  }
  return palette[Math.abs(hash) % palette.length];
};

const TravelTime = ({ arco, selectedDate, setSelectedDate, selectedData, radarSelected }) => {
  const [selectedRoadIndex, setSelectedRoadIndex] = React.useState(0);
  const [availableDates, setAvailableDates] = React.useState([]);
  const [isExpanded, setIsExpanded] = React.useState(false);

  // Normalize selectedDate to array
  const selectedDates = Array.isArray(selectedDate)
    ? selectedDate
    : (selectedDate ? [selectedDate] : []);

  // Sort dates
  const sortedSelectedDates = [...selectedDates].sort();
  const latestDate = sortedSelectedDates[0];

  // Group by hash if it's an array - do this before any returns
  const isMultipleRoads = Array.isArray(arco) && arco.length > 0;
  let roads = [];

  if (isMultipleRoads) {
    const roadsByHash = {};
    arco.forEach(element => {
      const hash = element.hash;
      if (!roadsByHash[hash]) {
        roadsByHash[hash] = [];
      }
      roadsByHash[hash].push(element);
    });

    roads = Object.keys(roadsByHash).map(hash => ({
      hash,
      elements: roadsByHash[hash]
    }));
  } else if (arco && !Array.isArray(arco)) {
    roads = [{ hash: arco.hash, elements: [arco] }];
  }

  // Get available dates from roads data - MUST be called before any returns
  useEffect(() => {
    if (!roads || roads.length === 0) {
      setAvailableDates([]);
      return;
    }

    const dates = new Set();
    roads.forEach(road => {
      road.elements?.forEach(el => {
        // Priority 1: t_start field (format: "2024-08-01 00:00:00")
        if (el.t_start && typeof el.t_start === 'string' && el.t_start.trim() !== '') {
          const parts = el.t_start.split(' ');
          if (parts.length >= 1) {
            const datePart = parts[0];
            if (datePart.match(/^\d{4}-\d{2}-\d{2}$/)) {
              dates.add(datePart);
              return; // Found date from t_start, skip other checks
            }
          }
        }

        // Priority 2: day_type + hour fields (saronno format)
        if (el.day_type && el.hour !== undefined && el.hour !== null && el.hour !== '') {
          // Use day_type as the date key (e.g., "festivo", "sabato", "feriale")
          dates.add(el.day_type);
          return;
        }

        // Priority 3: day_group_label field with date patterns
        // Handle both date formats:
        // - New format: "2024-08-01" (YYYY-MM-DD)
        // - Old format: "12,13,14-03-2024_0" (DD,DD,DD-MM-YYYY_0)
        if (el.day_group_label) {
          const datePart = el.day_group_label.split('_')[0];

          // Check if it's new format (YYYY-MM-DD)
          const newFormatMatch = datePart.match(/^(\d{4})-(\d{2})-(\d{2})$/);
          if (newFormatMatch) {
            // New format: already in YYYY-MM-DD
            dates.add(datePart);
          } else {
            // Old format: extract dates from DD,DD,DD-MM-YYYY
            const dateMatch = datePart.match(/^([\d,]+)-(\d+)-(\d+)$/);
            if (dateMatch) {
              const days = dateMatch[1].split(',');
              const month = dateMatch[2].padStart(2, '0');
              const year = dateMatch[3];

              // Add each day as a separate date
              days.forEach(day => {
                const formattedDate = `${year}-${month}-${day.padStart(2, '0')}`;
                dates.add(formattedDate);
              });
            } else {
              // Text-based day_group_label - use as-is
              dates.add(el.day_type);
            }
          }
        } else if (el.date) {
          dates.add(dayjs(el.date).format('YYYY-MM-DD'));
        }
      });
    });

    setAvailableDates(Array.from(dates).sort());
  }, [roads]);

  // Set LAST date as default if no date is selected, 
  // or restrict to single date if only one street is selected
  useEffect(() => {
    if (isMultipleRoads === false || (arco && Array.isArray(arco) && arco.length === 1)) {
      if (selectedDates.length > 1) {
        setSelectedDate(selectedDates[0]);
      }
    }

    if ((!selectedDate || (Array.isArray(selectedDate) && selectedDate.length === 0)) && availableDates.length > 0) {
      setSelectedDate(availableDates[0]);
    }
  }, [availableDates, selectedDate, setSelectedDate, arco, isMultipleRoads]);

  // Generate travel time chart data
  const generateTravelTimeData = () => {
    if (!selectedDates || selectedDates.length === 0) {
      return { chartData: [] };
    }

    // Filter to show only the selected road when multiple roads are selected
    const roadsToShow = roads.length > 1 ? [roads[selectedRoadIndex]] : roads;

    const dataByTime = new Map();
    const timeSlots = [];
    for (let h = 0; h < 24; h++) {
      timeSlots.push(`${h.toString().padStart(2, '0')}:00`);
    }
    timeSlots.forEach(time => {
      dataByTime.set(time, { time });
    });

    sortedSelectedDates.forEach(date => {
      const filteredElements = roadsToShow[0].elements?.filter(el => {
        // Priority 1: t_start field
        if (el.t_start && typeof el.t_start === 'string' && el.t_start.trim() !== '') {
          const parts = el.t_start.split(' ');
          if (parts.length >= 1) {
            const datePart = parts[0];
            if (datePart.match(/^\d{4}-\d{2}-\d{2}$/)) {
              return datePart === date;
            }
          }
        }

        // Priority 2: day_type
        if (el.day_type === date) return true;

        // Priority 3: day_group_label
        if (el.day_group_label) {
          const datePart = el.day_group_label.split('_')[0];
          if (datePart === date) return true;
          if (el.day_group_label.includes(date)) return true;
        } else if (el.date) {
          const elDate = dayjs(el.date).format('YYYY-MM-DD');
          return elDate === date;
        }
        return false;
      }) || [];

      filteredElements.forEach(el => {
        let time = '00:00';
        if (el.t_start && typeof el.t_start === 'string') {
          const parts = el.t_start.split(' ');
          if (parts.length >= 2) time = parts[1].substring(0, 5);
        } else if (el.hour !== undefined) {
          time = String(parseInt(el.hour, 10)).padStart(2, '0') + ':00';
        }

        // Get travel time in seconds from AvgTt field
        const travelTime = parseFloat(el.AvgTt) || parseFloat(el.travel_time_in_seconds) || 0;

        const entry = dataByTime.get(time) || { time };
        entry[`value_${date}`] = Math.round(travelTime);
        dataByTime.set(time, entry);
      });
    });

    // Average is computed as straight horizontal line from stats.avg

    const dataPoints = Array.from(dataByTime.values()).sort((a, b) => {
      const [aHour, aMin] = a.time.split(':').map(Number);
      const [bHour, bMin] = b.time.split(':').map(Number);
      return (aHour * 60 + aMin) - (bHour * 60 + bMin);
    });

    return {
      chartData: dataPoints
    };
  };

  const { chartData: chartDataToDisplay } = generateTravelTimeData();

  const stats = React.useMemo(() => {
    if (!chartDataToDisplay || chartDataToDisplay.length === 0 || !sortedSelectedDates || sortedSelectedDates.length === 0) return { max: 0, min: 0, avg: 0 };

    // Collect values from ALL selected dates
    const values = [];
    chartDataToDisplay.forEach(d => {
      sortedSelectedDates.forEach(date => {
        const v = d[`value_${date}`];
        if (v !== undefined && v !== null) {
          values.push(v);
        }
      });
    });

    if (values.length === 0) return { max: 0, min: 0, avg: 0 };

    const max = Math.max(...values);
    const min = Math.min(...values);
    const avg = values.reduce((a, b) => a + b, 0) / values.length;

    return { max, min, avg };
  }, [chartDataToDisplay, sortedSelectedDates]);

  // Get road name for display
  const roadName = React.useMemo(() => {
    if (!arco) return '';
    const elements = Array.isArray(arco) ? arco : [arco];
    if (elements.length > 0 && elements[0].name) return elements[0].name;
    return '';
  }, [arco]);

  // Only show for roads, not radar
  if (radarSelected && (!arco || arco.length === 0)) {
    return (
      <div className="proprieta-wrapper">
        <div className="proprieta-content" style={{ padding: '40px 24px', textAlign: 'center' }}>
          <p style={{ color: '#999', fontSize: '14px' }}>
            Seleziona una strada sulla mappa per visualizzare i tempi di percorrenza.
          </p>
        </div>
      </div>
    );
  }

  if (!arco || (Array.isArray(arco) && arco.length === 0)) {
    return (
      <div className="proprieta-wrapper">
        <div className="proprieta-content" style={{ padding: '40px 24px', textAlign: 'center' }}>
          <p style={{ color: '#999', fontSize: '14px' }}>
            Seleziona una strada sulla mappa per visualizzare i tempi di percorrenza.
          </p>
        </div>
      </div>
    );
  }


  // Render chart component
  const renderChart = (height = 300) => (
    <>
      {selectedDates.length > 0 && chartDataToDisplay?.length > 0 ? (
        <ResponsiveContainer width="100%" height={height}>
          <AreaChart data={chartDataToDisplay} margin={{ top: 28, right: 20, left: 35, bottom: 10 }}>
            <defs>
              {sortedSelectedDates.map((date, index) => {
                const color = DATE_COLORS[index % DATE_COLORS.length];
                return (
                  <linearGradient key={`gradient_${date}`} id={`travelTimeGradient_${index}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={color} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={color} stopOpacity={0} />
                  </linearGradient>
                );
              })}
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="time"
              tick={{ fontSize: 11, fill: '#666' }}
              axisLine={{ stroke: '#e0e0e0' }}
              interval="preserveStartEnd"
              ticks={TIME_TICKS}
            />
            <YAxis
              tick={{ fontSize: 11, fill: '#666' }}
              axisLine={{ stroke: '#e0e0e0' }}
              label={{ value: 'Secondi', position: 'insideLeft', angle: -90, offset: 0, style: { textAnchor: 'middle', fontSize: 12, fill: '#666' } }}
            />
            <Tooltip
              contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}
              itemStyle={{ fontSize: '12px' }}
              formatter={(value, name) => {
                return [`${value} s`, name];
              }}
            />


            {sortedSelectedDates.map((date, index) => {
              const color = DATE_COLORS[index % DATE_COLORS.length];
              return (
                <Area
                  key={date}
                  type="monotone"
                  dataKey={`value_${date}`}
                  stroke={color}
                  strokeWidth={2}
                  fill={`url(#travelTimeGradient_${index})`}
                  name={dayjs(date).format('DD/MM/YYYY')}
                  connectNulls={true}
                  activeDot={{ r: 5, strokeWidth: 0, fill: color }}
                />
              );
            })}

            {/* Average as straight horizontal line */}
            {stats.avg > 0 && (
              <ReferenceLine
                y={Math.round(stats.avg)}
                stroke={COLOR_AVG}
                strokeWidth={2}
                strokeDasharray="3 3"
                label={createAverageLabel({
                  text: `Media: ${Math.round(stats.avg)}`,
                  fill: COLOR_AVG,
                })}
              />
            )}
          </AreaChart>
        </ResponsiveContainer>
      ) : (
        <div className="chart-no-data">
          <p>⚠️ Nessun dato disponibile per questa strada nelle date selezionate.</p>
        </div>
      )}
    </>
  );

  // Check if road has any data at all
  const hasAnyData = availableDates.length > 0;

  return (
    <>
      <div className="proprieta-wrapper">
        <div className="proprieta-title">
          <img src={TravelTimeIcon} alt="Tempo di Percorrenza" className="proprieta-icon-img" />
          <h3>{roadName ? roadName : 'Tempo di Percorrenza'}</h3>
        </div>

        {/* Indicatori */}
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0 16px 16px 16px', marginTop: '10px' }}>
          <div style={{ textAlign: 'center' }}>
            <span style={{ fontSize: '11px', color: '#666', display: 'block' }}>VALORE MAX</span>
            <span style={{ fontSize: '16px', fontWeight: 'bold', color: '#333' }}>{stats.max}</span>
          </div>
          <div style={{ textAlign: 'center' }}>
            <span style={{ fontSize: '11px', color: '#666', display: 'block' }}>VALORE MIN</span>
            <span style={{ fontSize: '16px', fontWeight: 'bold', color: '#333' }}>{stats.min}</span>
          </div>
          <div style={{ textAlign: 'center' }}>
            <span style={{ fontSize: '11px', color: '#666', display: 'block' }}>VALORE MEDIO</span>
            <span style={{ fontSize: '16px', fontWeight: 'bold', color: '#333' }}>{stats.avg.toFixed(0)}</span>
          </div>
        </div>

        {/* Road selector buttons - always visible when 2 roads are selected */}
        {roads.length > 1 && (
          <div className="travel-time-road-selector">
            <button
              onClick={() => setSelectedRoadIndex(0)}
              className={`travel-time-road-btn road-1 ${selectedRoadIndex === 0 ? 'active' : ''}`}
            >
              {roads[0]?.elements[0]?.name || roads[0]?.elements[0]?.StreetName || "Strada 1"}
            </button>
            <button
              onClick={() => setSelectedRoadIndex(1)}
              className={`travel-time-road-btn road-2 ${selectedRoadIndex === 1 ? 'active' : ''}`}
            >
              {roads[1]?.elements[0]?.name || roads[1]?.elements[0]?.StreetName || "Strada 2"}
            </button>
          </div>
        )}

        {/* Always show date selector and chart area */}
        <div className="proprieta-content anomaly-content">
          <p className="proprieta-description anomaly-description">
            Seleziona le date (max 7) per visualizzare i valori.
          </p>
          {hasAnyData ? (
            <>
              <Select
                mode={(arco && Array.isArray(arco) && arco.length === 1) ? undefined : "multiple"}
                maxTagCount={0}
                maxTagPlaceholder={`${selectedDates.length} date selezionate`}
                placeholder="Seleziona data"
                className="anomaly-date-picker"
                value={(arco && Array.isArray(arco) && arco.length === 1) ? (selectedDates[0] || selectedDate) : selectedDates}
                onChange={(val) => {
                  if (arco && Array.isArray(arco) && arco.length === 1) {
                    setSelectedDate(val);
                  } else if (val.length <= 7) {
                    setSelectedDate(val);
                  }
                }}
                getPopupContainer={(trigger) => trigger.parentElement}
                popupStyle={{ zIndex: 9999 }}
                style={{ borderRadius: "20px" }}
              >
                {availableDates.map(date => {
                  // Safe date formatting - handle text-based dates
                  let displayDate = date;
                  try {
                    if (date && dayjs(date).isValid() && date.match(/^\d{4}-\d{2}-\d{2}$/)) {
                      displayDate = dayjs(date).format('DD/MM/YYYY');
                    }
                  } catch (e) {
                    // Keep original text for non-date values
                  }
                  return (
                    <Select.Option key={date} value={date}>
                      {displayDate}
                    </Select.Option>
                  );
                })}
              </Select>
            </>
          ) : (
            <div style={{ padding: '12px', textAlign: 'center', color: '#999', fontSize: '13px' }}>
              Nessuna data disponibile per questa strada.
            </div>
          )}
        </div>

        {/* Selected date tags - below the card, aligned right */}
        {selectedDates.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', padding: '8px 16px', justifyContent: 'flex-end' }}>
            {sortedSelectedDates.map((date, index) => {
              let displayDate = date;
              try {
                if (date && dayjs(date).isValid() && date.match(/^\d{4}-\d{2}-\d{2}$/)) {
                  displayDate = dayjs(date).format('DD/MM/YYYY');
                }
              } catch (e) { }
              const color = DATE_COLORS[index % DATE_COLORS.length];
              return (
                <span
                  key={date}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                    padding: '2px 8px', borderRadius: '12px', fontSize: '11px',
                    background: `${color}20`, border: `1px solid ${color}`,
                    color: color, fontWeight: 500, cursor: 'pointer',
                  }}
                  onClick={() => setSelectedDate(selectedDates.filter(d => d !== date))}
                >
                  {displayDate}
                  <span style={{ fontSize: '13px', lineHeight: 1 }}>×</span>
                </span>
              );
            })}
          </div>
        )}

        {/* Travel Time Chart Card - Always show, with no-data message if needed */}
        <div className="travel-time-chart-container">
          {selectedDates.length > 0 && chartDataToDisplay?.length > 0 && (
            <ExpandButton onClick={() => setIsExpanded(true)} />
          )}
          {renderChart()}
        </div>
      </div>

      {/* Expanded Chart Modal */}
      <Modal
        open={isExpanded}
        onCancel={() => setIsExpanded(false)}
        footer={null}
        width="95%"
        centered
        title="Tempo di Percorrenza - Vista Espansa"
        destroyOnClose
        zIndex={9999}
        style={{ top: 20 }}
      >
        <div className="travel-time-modal-content">
          {renderChart(500)}
        </div>
      </Modal>
    </>
  );
};

export default TravelTime;
